// Canonical sharing-UI template — the share modal.
// Grant a saved calculation to another account by email, and create/copy/revoke
// anonymous view-only links with a mandatory expiry.

import React, { useCallback, useEffect, useState } from "react";
import { NumberInput } from "@structura/ui";
import type { SharingClient, ShareGrant, ShareLinkInfo } from "./types";

const LINK_EXPIRY_DEFAULT_DAYS = 7;
const LINK_EXPIRY_MAX_DAYS = 30;

export interface ShareEditorProps {
  client: SharingClient;
  resourceType: string;
  resourceId: string;
  title: string;
  onClose: () => void;
}

export const ShareEditor: React.FC<ShareEditorProps> = ({ client, resourceType, resourceId, title, onClose }) => {
  const [grants, setGrants] = useState<ShareGrant[]>([]);
  const [links, setLinks] = useState<ShareLinkInfo[]>([]);
  const [email, setEmail] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [linkExpiryDays, setLinkExpiryDays] = useState(LINK_EXPIRY_DEFAULT_DAYS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [g, l] = await Promise.all([
        client.listShares(resourceType, resourceId),
        client.listLinks(resourceType, resourceId),
      ]);
      setGrants(g);
      setLinks(l);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke hente delinger.");
    }
  }, [client, resourceType, resourceId]);

  useEffect(() => { refresh(); }, [refresh]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Handlingen feilet.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddShare = () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    run(async () => {
      await client.addShare(resourceType, resourceId, trimmed);
      setEmail("");
    });
  };

  const handleCreateLink = () => run(async () => {
    await client.createLink(resourceType, resourceId, linkExpiryDays, linkLabel.trim());
    setLinkLabel("");
  });

  const copyLinkUrl = async (token: string) => {
    await navigator.clipboard.writeText(`${window.location.origin}/link/${token}`);
    setCopiedToken(token);
    window.setTimeout(() => setCopiedToken((current) => (current === token ? null : current)), 1500);
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "1rem" }}
      onClick={onClose}
    >
      <div className="sm-card" style={{ maxWidth: "620px", width: "100%", maxHeight: "90vh", overflowY: "auto", background: "var(--sm-surface, #fff)", borderRadius: "8px" }} onClick={(e) => e.stopPropagation()}>
        <div className="sm-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem" }}>
          <h2 style={{ margin: 0 }}><i className="bi bi-share"></i> Del «{title}»</h2>
          <button type="button" className="sm-btn sm-btn-sm sm-btn-secondary" onClick={onClose}><i className="bi bi-x-lg"></i></button>
        </div>
        <div className="sm-card-body" style={{ padding: "0 1rem 1rem" }}>
          {error && (
            <div className="sm-error-box" style={{ marginBottom: "1rem" }}>
              <strong>Feil:</strong>
              <pre className="sm-error-details">{error}</pre>
            </div>
          )}

          <h3>Del med bruker</h3>
          <p style={{ fontSize: "0.85rem", color: "var(--sm-text-muted, #64748b)" }}>
            Mottakeren må ha en StructuraMatrix-konto og finner beregningen under «Delt med meg».
          </p>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
            <input
              type="email"
              className="sm-form-control"
              placeholder="epost@firma.no"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddShare(); }}
              style={{ flex: 1 }}
            />
            <button type="button" className="sm-btn sm-btn-primary" onClick={handleAddShare} disabled={busy || !email.trim()}>Del</button>
          </div>
          {grants.length > 0 && (
            <table className="sm-table">
              <tbody>
                {grants.map((grant) => (
                  <tr key={grant.id}>
                    <td>{grant.granteeName || grant.granteeEmail}<br /><span style={{ fontSize: "0.8rem", color: "var(--sm-text-muted, #64748b)" }}>{grant.granteeEmail}</span></td>
                    <td style={{ textAlign: "right" }}>
                      <button type="button" className="sm-btn sm-btn-sm sm-btn-secondary" disabled={busy}
                        onClick={() => run(() => client.revokeShare(resourceType, resourceId, grant.granteeUserId))}>
                        Fjern
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ marginTop: "1.25rem" }}>Delingslenker</h3>
          <p style={{ fontSize: "0.85rem", color: "var(--sm-text-muted, #64748b)" }}>
            Alle med lenken kan se beregningen (kun visning) til den utløper eller trekkes tilbake.
          </p>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <input
              type="text"
              className="sm-form-control"
              placeholder="Etikett (valgfritt)"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              style={{ flex: "1 1 180px" }}
            />
            <NumberInput
              className="sm-form-control"
              integer
              value={linkExpiryDays}
              onCommit={(days) => setLinkExpiryDays(Math.min(LINK_EXPIRY_MAX_DAYS, Math.max(1, days)))}
              title="Gyldighet i dager (1–30)"
              style={{ width: "5.5rem" }}
            />
            <button type="button" className="sm-btn sm-btn-primary" onClick={handleCreateLink} disabled={busy}>Opprett lenke</button>
          </div>
          {links.length > 0 && (
            <table className="sm-table">
              <tbody>
                {links.map((link) => (
                  <tr key={link.token}>
                    <td>
                      {link.label && <div>{link.label}</div>}
                      <code style={{ fontSize: "0.75rem", wordBreak: "break-all" }}>{`${window.location.origin}/link/${link.token}`}</code>
                      <div style={{ fontSize: "0.8rem", color: "var(--sm-text-muted, #64748b)" }}>Utløper {new Date(link.expiresAt).toLocaleDateString("nb-NO")}</div>
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="sm-btn sm-btn-sm sm-btn-secondary" onClick={() => copyLinkUrl(link.token)} style={{ marginRight: "0.25rem" }}>
                        {copiedToken === link.token ? "Kopiert!" : "Kopier"}
                      </button>
                      <button type="button" className="sm-btn sm-btn-sm sm-btn-secondary" disabled={busy}
                        onClick={() => run(() => client.revokeLink(resourceType, resourceId, link.token))}>
                        Trekk tilbake
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareEditor;
